import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { aiAgentsData } from '@/mocks/homepageData';

const productNavItems = [
  { path: '/markets', label: 'Markets' },
  { path: '/ai-agents', label: 'AI Agents' },
  { path: '/vaults', label: 'Vaults' },
  { path: '/leaderboard', label: 'Leaderboard' },
  { path: '/fees', label: 'Fees' },
];

const riskColorMap: Record<string, string> = {
  'neon-green': 'text-[#00ff88]',
  warning: 'text-[#ffaa00]',
  danger: 'text-[#ff5566]',
};

export default function AIAgentsPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';

  return (
    <CompanyLayout
      title={t('aiAgents.title', 'AI Agents')}
      subtitle={t('aiAgents.subtitle', 'Deploy autonomous AI agents that trade 24/7 with machine-learned strategies')}
      category="Product"
      navItems={productNavItems}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {aiAgentsData.map((agent) => (
          <div key={agent.id} className={`p-5 rounded-xl border ${borderLight} ${bgCard}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`font-semibold ${textPrimary}`}>{agent.name}</h3>
                <p className={`text-xs mt-1 ${textSecondary}`}>{agent.description}</p>
              </div>
              <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className={`p-3 rounded-lg border ${borderLight}`}>
                <div className={`text-xs ${textMuted} mb-1`}>{t('aiAgents.pnl30d', 'PNL (30d)')}</div>
                <div className="font-mono font-bold text-[#00ff88]">+{agent.pnl}%</div>
              </div>
              <div className={`p-3 rounded-lg border ${borderLight}`}>
                <div className={`text-xs ${textMuted} mb-1`}>{t('aiAgents.winRate', 'Win Rate')}</div>
                <div className={`font-mono font-bold ${textPrimary}`}>{agent.winRate}%</div>
              </div>
              <div className={`p-3 rounded-lg border ${borderLight}`}>
                <div className={`text-xs ${textMuted} mb-1`}>{t('aiAgents.trades24h', 'Trades (24h)')}</div>
                <div className={`font-mono font-bold ${textPrimary}`}>{agent.trades24h.toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className={textMuted}>{t('aiAgents.avgHold', 'Avg Hold')}: <span className={textSecondary}>{agent.avgHoldTime}</span></span>
              <span className={textMuted}>{t('aiAgents.risk', 'Risk')}: <span className={`font-medium ${riskColorMap[agent.riskColor] || 'text-[#ffaa00]'}`}>{agent.riskLevel}</span></span>
            </div>
          </div>
        ))}
      </div>
    </CompanyLayout>
  );
}